"use client";

import dynamic from "next/dynamic";
import { memo } from "react";
import Link from "next/link";
import { cn } from "@/lib/utils";
import {
  isIntegrationMainServiceItem,
  isInternalMainServiceItem,
  type MainServiceItemData,
} from "./mainServiceItems";
import { Typography } from "@/components/Typography";

const Lottie = dynamic(() => import("lottie-react"), {
  ssr: false,
});

type MainServiceItemProps = {
  item: MainServiceItemData;
  badge?: string;
  isMobile: boolean;
  isSelected: boolean;
  isLoading: boolean;
  isComingSoon?: boolean;
  onClick: (item: MainServiceItemData) => void;
};

const MainServiceItem = (props: MainServiceItemProps) => {
  const {
    item,
    badge,
    isMobile,
    isSelected,
    isLoading,
    isComingSoon = false,
    onClick,
  } = props;
  const isIntegrationService = isIntegrationMainServiceItem(item);
  const isClickable =
    isInternalMainServiceItem(item) || isIntegrationMainServiceItem(item);

  const content = (
    <>
      {badge ? (
        <span className="pointer-events-none absolute left-1/2 top-1.5 z-20 inline-flex h-6 -translate-x-1/2 -translate-y-1/2 items-center whitespace-nowrap rounded-full bg-[#FF0020] px-2.5 text-[12px] font-semibold text-white">
          {badge}
        </span>
      ) : null}

      <div
        className={cn(
          "relative z-0 flex h-16 w-16 items-center justify-center rounded-[12px] transition-shadow md:h-20 md:w-20 md:rounded-[20px]",
          !isIntegrationService &&
            "group-hover:shadow-[0_0_0_2px_#3BDAFF] group-focus-visible:shadow-[0_0_0_2px_#3BDAFF]",
          isSelected && "shadow-[0_0_0_2px_#3BDAFF]",
          isLoading && "opacity-70",
        )}
      >
        {item.iconSrc ? (
          <Lottie
            animationData={item.iconSrc}
            autoplay
            loop
            className={cn(
              "h-16 w-16 md:h-[72px] md:w-[72px] transition-[filter] duration-200",
              !isSelected && "md:grayscale",
            )}
          />
        ) : null}
      </div>

      <Typography
        variant="button-sm"
        className={cn(
          "whitespace-nowrap text-center font-semibold",
          isSelected ? "text-secondary" : "text-body",
        )}
      >
        {item.label}
      </Typography>
    </>
  );

  const itemClassName = item.mobileOnly
    ? "group relative flex min-w-0 flex-col items-center gap-1 rounded-2xl p-1.5 text-center md:hidden"
    : "group relative flex min-w-0 flex-col items-center gap-1 rounded-2xl p-1.5 text-center transition md:gap-2.5";

  // Navigate directly to internal service pages on mobile.
  if (isMobile && isInternalMainServiceItem(item) && !isComingSoon) {
    return (
      <Link href={item.href} className={itemClassName}>
        {content}
      </Link>
    );
  }

  // Handle desktop selection and partner integration actions as buttons.
  if (isClickable) {
    return (
      <button
        type="button"
        onClick={() => onClick(item)}
        aria-pressed={isSelected}
        disabled={isLoading}
        className={itemClassName}
      >
        {content}
      </button>
    );
  }

  // Render unavailable services as non-interactive items.
  return <div className={itemClassName}>{content}</div>;
};

export default memo(MainServiceItem);
